import React, { useState, useEffect } from 'react';
import { registrationsApi, PastAttendee, Event, RegistrationCreate } from '../api/client';
import { XMarkIcon, MagnifyingGlassIcon, UserGroupIcon, CalendarIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';

interface PastAttendeesSelectorProps {
  event: Event;
  onSelect: (attendees: RegistrationCreate[]) => void;
  onClose: () => void;
}

const PastAttendeesSelector: React.FC<PastAttendeesSelectorProps> = ({ event, onSelect, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [attendees, setAttendees] = useState<PastAttendee[]>([]);
  const [filteredAttendees, setFilteredAttendees] = useState<PastAttendee[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState<string>('');
  const [eventFilter, setEventFilter] = useState<string>('');
  const [eventsIncluded, setEventsIncluded] = useState<Array<{id: number; title: string; date: string}>>([]);
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadPastAttendees();
  }, []);

  useEffect(() => {
    filterAttendees();
  }, [attendees, searchQuery, relationshipFilter, eventFilter, statusFilters]);

  const loadPastAttendees = async () => {
    try {
      setLoading(true);
      const response = await registrationsApi.getPastAttendees(event.id);
      setAttendees(response.attendees);
      setFilteredAttendees(response.attendees);
      setEventsIncluded(response.events_included);
    } catch (error) {
      console.error('Failed to load past attendees:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAttendees = () => {
    let filtered = [...attendees];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.email.toLowerCase().includes(query) ||
        a.name?.toLowerCase().includes(query) ||
        a.company?.toLowerCase().includes(query) ||
        a.role?.toLowerCase().includes(query)
      );
    }

    // Relationship type filter
    if (relationshipFilter) {
      filtered = filtered.filter(a => a.relationship_type === relationshipFilter);
    }

    // Event filter
    if (eventFilter) {
      const eventId = parseInt(eventFilter);
      filtered = filtered.filter(a =>
        a.events_attended.some(e => e.id === eventId)
      );
    }

    // Status filter - only apply if some (but not all) checkboxes are selected
    if (statusFilters.size > 0) {
      filtered = filtered.filter(a =>
        a.events_attended.some(e =>
          statusFilters.has(e.status || 'pending')
        )
      );
    }

    setFilteredAttendees(filtered);
  };

  const toggleSelectAll = () => {
    if (selectedEmails.size === filteredAttendees.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(filteredAttendees.map(a => a.email)));
    }
  };

  const toggleSelect = (email: string) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedEmails(newSelected);
  };

  const handleImport = () => {
    const selectedAttendees = attendees.filter(a => selectedEmails.has(a.email));
    const registrations: RegistrationCreate[] = selectedAttendees.map(a => ({
      email: a.email,
      name: a.name || `${a.given_name || ''} ${a.family_name || ''}`.trim(),
      company: a.company || '',
      given_name: a.given_name,
      family_name: a.family_name,
      role: a.role,
      relationship_type: a.relationship_type
    }));
    onSelect(registrations);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRelationshipTypes = () => {
    const types = new Set<string>();
    attendees.forEach(a => {
      if (a.relationship_type) types.add(a.relationship_type);
    });
    return Array.from(types);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Import from Past Events</h2>
              <p className="text-gray-600 mt-1">
                Select attendees from previous events to invite to {event.title}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Status Filters */}
          <div className="mt-4 mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status:</label>
            <div className="flex flex-wrap gap-3">
              {[
                { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
                { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
                { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
                { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-800' },
                { value: 'checked_in', label: 'Checked In', color: 'bg-blue-100 text-blue-800' },
                { value: 'checked_out', label: 'Checked Out', color: 'bg-purple-100 text-purple-800' },
              ].map((status) => (
                <label key={status.value} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilters.has(status.value)}
                    onChange={(e) => {
                      const newFilters = new Set(statusFilters);
                      if (e.target.checked) {
                        newFilters.add(status.value);
                      } else {
                        newFilters.delete(status.value);
                      }
                      setStatusFilters(newFilters);
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Other Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, company..."
                className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Relationship Type Filter */}
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={relationshipFilter}
              onChange={(e) => setRelationshipFilter(e.target.value)}
            >
              <option value="">All relationship types</option>
              {getRelationshipTypes().map(type => (
                <option key={type} value={type}>
                  {type.replace('_', ' ')}
                </option>
              ))}
            </select>

            {/* Event Filter */}
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
            >
              <option value="">All past events</option>
              {eventsIncluded.map(evt => (
                <option key={evt.id} value={evt.id}>
                  {evt.title} ({formatDate(evt.date)})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredAttendees.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">
                {searchQuery || relationshipFilter || eventFilter
                  ? 'No attendees match your filters'
                  : 'No past attendees found'}
              </p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="mb-4 flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={selectedEmails.size === filteredAttendees.length && filteredAttendees.length > 0}
                    onChange={toggleSelectAll}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select all ({filteredAttendees.length} attendees)
                  </span>
                </label>
                {selectedEmails.size > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedEmails.size} selected
                  </span>
                )}
              </div>

              {/* Attendee List */}
              <div className="space-y-2">
                {filteredAttendees.map(attendee => (
                  <div
                    key={attendee.email}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedEmails.has(attendee.email)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => toggleSelect(attendee.email)}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedEmails.has(attendee.email)
                            ? 'border-blue-600 bg-blue-600'
                            : 'border-gray-300 bg-white'
                        }`}>
                          {selectedEmails.has(attendee.email) && (
                            <CheckIcon className="w-3 h-3 text-white" />
                          )}
                        </div>
                      </div>

                      <div className="ml-4 flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {attendee.name || attendee.email}
                            </p>
                            {attendee.name && (
                              <p className="text-sm text-gray-600">{attendee.email}</p>
                            )}
                            {attendee.company && (
                              <p className="text-sm text-gray-600 flex items-center mt-1">
                                <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                                {attendee.company}
                                {attendee.role && ` • ${attendee.role}`}
                              </p>
                            )}
                          </div>

                          {attendee.relationship_type && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {attendee.relationship_type.replace('_', ' ')}
                            </span>
                          )}
                        </div>

                        {/* Event History with Status Badges */}
                        <div className="mt-2">
                          <div className="flex items-center text-xs text-gray-500">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            <span>
                              {attendee.total_events_attended} event{attendee.total_events_attended !== 1 ? 's' : ''}
                              {attendee.last_attendance_date && (
                                <> • Last: {formatDate(attendee.last_attendance_date)}</>
                              )}
                            </span>
                          </div>
                          {/* Status badges for recent events */}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {attendee.events_attended.slice(0, 3).map((evt, idx) => {
                              const statusColors: Record<string, string> = {
                                pending: 'bg-yellow-100 text-yellow-800',
                                approved: 'bg-green-100 text-green-800',
                                rejected: 'bg-red-100 text-red-800',
                                cancelled: 'bg-gray-100 text-gray-800',
                                checked_in: 'bg-blue-100 text-blue-800',
                                checked_out: 'bg-purple-100 text-purple-800'
                              };
                              const status = evt.status || 'pending';
                              return (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}
                                  title={`${evt.title} - ${formatDate(evt.date)}`}
                                >
                                  {status.replace('_', ' ')}
                                </span>
                              );
                            })}
                            {attendee.events_attended.length > 3 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                +{attendee.events_attended.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={selectedEmails.size === 0}
              className={`px-4 py-2 rounded-lg font-medium ${
                selectedEmails.size > 0
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Import {selectedEmails.size > 0 ? `${selectedEmails.size} Selected` : 'Selected'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastAttendeesSelector;